import {MAX_QUERIES_TO_MARK_FOR_PREHEATING} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {type RefObject, useEffect, useMemo, useState} from 'react'

import {useCachePreheatIssueViewerQueries} from '../../hooks/use-cache-preheat-issueviewer'
import type {ItemIdentifier} from '../../types/issue'
import {gatherIssueLinks} from '../../utils/helpers'
import {parseGitHubIssueUrl} from '../../utils/urls'

type TimelineLinkPreheatProps = {
  /** The rendered timeline, scanned for links to other issues. */
  timelineContainerRef: RefObject<HTMLDivElement | null>
  issueUrl: string
  /** Rescan when the timeline grows. */
  totalItemCount: number
}

/**
 * Warms the Relay cache for issues linked from the timeline. Its own component because it needs a
 * Relay environment and warns without one, so a route with no Relay leaves it unmounted.
 */
export function TimelineLinkPreheat({timelineContainerRef, issueUrl, totalItemCount}: TimelineLinkPreheatProps) {
  const [issueLinks, setIssueLinks] = useState<string[]>([])

  useEffect(() => {
    const links = gatherIssueLinks(timelineContainerRef.current)
    setIssueLinks(links)
  }, [totalItemCount, timelineContainerRef])

  const issuesWithLinks: Array<Omit<ItemIdentifier, 'type'>> = useMemo(() => {
    const currentIssue = parseGitHubIssueUrl(issueUrl)
    const seen = new Map<string, Omit<ItemIdentifier, 'type'>>()
    for (const link of issueLinks) {
      const parsed = parseGitHubIssueUrl(link)
      if (!parsed) continue
      // No need to preheat what we're already viewing
      if (
        currentIssue &&
        parsed.owner === currentIssue.owner &&
        parsed.repo === currentIssue.repo &&
        parsed.number === currentIssue.number
      ) {
        continue
      }
      const key = `${parsed.owner}/${parsed.repo}#${parsed.number}`
      if (!seen.has(key)) {
        seen.set(key, parsed)
        if (seen.size >= MAX_QUERIES_TO_MARK_FOR_PREHEATING) break
      }
    }
    return Array.from(seen.values())
  }, [issueLinks, issueUrl])

  useCachePreheatIssueViewerQueries({
    issues: issuesWithLinks,
    source: 'IssueTimeline',
  })

  return null
}
